import { ApolloError } from '@apollo/client'
import { ApiErrorCode, ApiErrorType } from 'types/errors'

function hasErrorCode(error: unknown, code: ApiErrorCode): boolean {
  let foundMatchingCode = false
  const graphQLErrors = (error as ApolloError)?.graphQLErrors || []
  for (const gqlError of graphQLErrors) {
    if (gqlError?.extensions?.code === code) {
      foundMatchingCode = true
    }
  }
  return foundMatchingCode
}

function hasErrorType(error: unknown, type: ApiErrorType): boolean {
  let foundMatchingType = false
  const graphQLErrors = (error as ApolloError)?.graphQLErrors || []
  for (const gqlError of graphQLErrors) {
    if (gqlError?.extensions?.type === type) {
      foundMatchingType = true
    }
  }
  return foundMatchingType
}

/**
 * Returns true when an ApolloError contains a ValidationError from the Beluga
 * API that matches the given string.
 */
function containsValidationError(error: unknown, message: string): boolean {
  const errorMessages = getValidationErrors(error)
  return errorMessages.some((m) => m.match(message))
}

/**
 * Returns a list of messages for fields with errors when an ApolloError
 * contains a ValidationError from the Beluga API.
 *
 * This is similar to `Ecto.Changeset.traverse_errors` in the Nemo codebase.
 */
function getValidationErrors(error: any): Array<string> {
  if (error?.graphQLErrors) {
    const graphQLErrors = (error as ApolloError).graphQLErrors
    const fields = graphQLErrors
      .filter((e) => e.extensions?.code === 'validation_error')
      .flatMap((e) =>
        typeof e.extensions?.fields === 'object'
          ? Object.entries(e.extensions.fields)
          : []
      ) as Array<[string, Array<string>]>
    return fields.flatMap(([key, messages]) => {
      return messages.map((message) => `${key} ${message}`)
    })
  }
  return []
}

const errorUtils = {
  hasErrorCode,
  hasErrorType,
  containsValidationError,
  getValidationErrors
}

export default errorUtils
